import React, { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import axios from "axios";
import {
    FormError,
    FormField,
    SentMessage,
    SpamProtection,
    turnstileRequired,
} from "../site/FormBits";

// Validation schema with Yup
const validationSchema = Yup.object().shape({
    firstName: Yup.string()
        .required("Vorname ist erforderlich")
        .min(2, "Vorname muss mindestens 2 Zeichen lang sein"),
    lastName: Yup.string()
        .required("Nachname ist erforderlich")
        .min(2, "Nachname muss mindestens 2 Zeichen lang sein"),
    email: Yup.string()
        .required("E-Mail ist erforderlich")
        .email("Ungültige E-Mail-Adresse"),
    phoneNumber: Yup.string()
        .required("Telefonnummer ist erforderlich")
        .matches(/^[0-9\s-]+$/, "Ungültige Telefonnummer"),
    message: Yup.string()
        .required("Nachricht ist erforderlich")
        .min(10, "Nachricht muss mindestens 10 Zeichen lang sein"),
    website: Yup.string().max(0, "Ungültige Anfrage"),
    formStartedAt: Yup.number().required(),
    turnstileToken: Yup.string().test(
        "turnstile-required",
        "Bitte bestätigen Sie, dass Sie kein Roboter sind.",
        turnstileRequired,
    ),
});

export default function ContactForm() {
    const [sent, setSent] = useState(false);
    const [errorMessage, setErrorMessage] = useState(null);
    const [loading, setLoading] = useState(false);
    const turnstileRef = useRef(null);

    const {
        register,
        handleSubmit,
        setValue,
        formState: { errors },
    } = useForm({
        resolver: yupResolver(validationSchema),
    });

    useEffect(() => {
        setValue("formStartedAt", Date.now());
        setValue("website", "");
    }, [setValue]);

    const onSubmit = async (data) => {
        setLoading(true);
        setErrorMessage(null);
        try {
            await axios.post("/api/public/contact", data);
            setSent(true);
        } catch (error) {
            setErrorMessage(
                error?.response?.data?.message ||
                    "Fehler beim Senden der Anfrage. Bitte versuchen Sie es erneut.",
            );
            turnstileRef.current?.reset();
        } finally {
            setLoading(false);
        }
    };

    if (sent) {
        return <SentMessage title="Danke, Ihre Nachricht ist angekommen." text="Wir melden uns bei Ihnen." />;
    }

    return (
        <form
            onSubmit={handleSubmit(onSubmit)}
            noValidate
            className="reveal d2 grid min-w-0 grid-cols-1 desk:grid-cols-2 gap-4"
        >
            <FormField
                label="Vorname"
                autoComplete="given-name"
                error={errors.firstName?.message}
                {...register("firstName")}
            />
            <FormField
                label="Nachname"
                autoComplete="family-name"
                error={errors.lastName?.message}
                {...register("lastName")}
            />
            <FormField
                label="E-Mail"
                type="email"
                autoComplete="email"
                error={errors.email?.message}
                {...register("email")}
            />
            <FormField
                label="Telefonnummer"
                type="tel"
                autoComplete="tel"
                error={errors.phoneNumber?.message}
                {...register("phoneNumber")}
            />
            <FormField
                label="Nachricht"
                as="textarea"
                rows={4}
                className="col-span-full"
                error={errors.message?.message}
                {...register("message")}
            />
            <SpamProtection
                register={register}
                setValue={setValue}
                turnstileRef={turnstileRef}
                error={errors.turnstileToken?.message}
            />
            <FormError>{errorMessage}</FormError>
            <button type="submit" disabled={loading} className="btn btn-primary col-span-full mt-2 py-[18px]">
                {loading ? "Wird gesendet …" : "Anfrage absenden"}
            </button>
        </form>
    );
}
